import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    LocalHospital as EmergencyIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintEmergencyFormPage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const [selectedStudentId, setSelectedStudentId] = useState<number | 'blank'>('blank');
    const printableRef = useRef<HTMLDivElement>(null);

    const selectedStudent = useMemo(() => {
        if (selectedStudentId === 'blank') return null;
        return students.find(s => s.id === selectedStudentId) || null;
    }, [students, selectedStudentId]);

    const studentGroup = useMemo(() => {
        if (!selectedStudent) return '';
        const gs = groupStudents.find(g => g.student_id === selectedStudent.id && g.status !== 'Dropped');
        if (gs) {
            const grp = groups.find(g => g.id === gs.group_id);
            if (grp) return grp.name;
        }
        return selectedStudent.school_level || '';
    }, [selectedStudent, groupStudents, groups]);

    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}-${currentYear + 1}`;

    const studentFullName = selectedStudent ? `${selectedStudent.last_name} ${selectedStudent.first_name}`.trim() : '—';
    const dob = selectedStudent?.date_of_birth || '—';
    const classLabel = studentGroup || '—';
    const address = selectedStudent?.address || '—';
    const bloodGroup = selectedStudent?.blood_group || '—';
    const allergies = selectedStudent?.has_allergies
        ? (selectedStudent.allergy_details || (language === 'ar' ? 'نعم' : 'Oui'))
        : (language === 'ar' ? 'لا يوجد' : language === 'en' ? 'None' : 'Aucune');
    const chronic = selectedStudent?.has_health_issues
        ? (selectedStudent.health_other_detail || (language === 'ar' ? 'نعم' : 'Oui'))
        : (language === 'ar' ? 'لا يوجد' : language === 'en' ? 'None' : 'Aucune');

    const parent1Name = selectedStudent?.guardian_first_name
        ? `${selectedStudent.guardian_first_name} ${selectedStudent.guardian_last_name || ''} · ${selectedStudent.guardian_phone || ''}`
        : '—';
    const parent2Name = selectedStudent?.emergency_contact || '—';

    // Export Handlers
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الحقل' : language === 'en' ? 'Field' : 'Champ',
            language === 'ar' ? 'القيمة' : language === 'en' ? 'Value' : 'Valeur'
        ];
        const rows = [
            [language === 'ar' ? 'التلميذ' : language === 'en' ? 'Student' : 'Apprenant', studentFullName],
            [language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Birth Date' : 'Date de naissance', dob],
            [language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe', classLabel],
            [language === 'ar' ? 'العنوان' : language === 'en' ? 'Address' : 'Adresse', address],
            [language === 'ar' ? 'الزمرة الدموية' : language === 'en' ? 'Blood Group' : 'Groupe sanguin', bloodGroup],
            [language === 'ar' ? 'الولي 1' : language === 'en' ? 'Parent 1' : 'Parent 1', parent1Name],
            [language === 'ar' ? 'الولي 2' : language === 'en' ? 'Parent 2' : 'Parent 2', parent2Name],
            [language === 'ar' ? 'الحساسية' : language === 'en' ? 'Allergies' : 'Allergies', allergies],
            [language === 'ar' ? 'مرض مزمن' : language === 'en' ? 'Chronic' : 'Maladie chronique', chronic]
        ];
        exportToExcel(`Fiche_Urgence_${selectedStudent?.code || 'Vierge'}`, 'Urgence', headers, rows);
    };

    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'بطاقة الطوارئ والترخيص بالعلاج' : language === 'en' ? 'Emergency Form' : "Fiche d'urgence";
        exportToWord(`Fiche_Urgence_${selectedStudent?.code || 'Vierge'}`, title, printableRef.current.innerHTML, isRtl);
    };

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '6px 8px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار' : language === 'en' ? 'Select' : 'Choisir'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'blank' ? 'blank' : Number(e.target.value))}
                >
                    <MenuItem value="blank">
                        {language === 'ar' ? '— بطاقة فارغة —' : language === 'en' ? '— Blank Form —' : '— Fiche vierge —'}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'بطاقة الطوارئ والترخيص بالعلاج' : language === 'en' ? 'Emergency Form' : "Fiche d'urgence et autorisation de soins"}
            icon={<EmergencyIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ maxWidth: 820, margin: '0 auto', color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'بطاقة الطوارئ والترخيص بالعلاج' : language === 'en' ? 'EMERGENCY FORM' : "FICHE D'URGENCE"}
                    subtitle={`${language === 'ar' ? 'السنة الدراسية' : language === 'en' ? 'Academic Year' : 'Année scolaire'} ${academicYear}`}
                    showBorderBottom
                />

                <Box sx={{ my: 2 }}>
                    {/* Top Identity Section with Photo Frame */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                        <table style={{ flex: 1, borderCollapse: 'collapse', fontSize: 12 }}>
                            <tbody>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'التلميذ' : language === 'en' ? 'Student' : 'Apprenant'}
                                    </td>
                                    <td style={cellStyle(undefined, { fontWeight: 600 })}>{studentFullName}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Birth Date' : 'Date de naissance'}
                                    </td>
                                    <td style={cellStyle()}>{dob}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                                    </td>
                                    <td style={cellStyle()}>{classLabel}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'العنوان' : language === 'en' ? 'Address' : 'Adresse'}
                                    </td>
                                    <td style={cellStyle()}>{address}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'الزمرة الدموية' : language === 'en' ? 'Blood Group' : 'Groupe sanguin'}
                                    </td>
                                    <td style={cellStyle()}>{bloodGroup}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'الطبيب المعالج' : language === 'en' ? 'Treating Physician' : 'Médecin traitant'}
                                    </td>
                                    <td style={cellStyle()}>—</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Photo Box */}
                        <div style={{
                            width: 120,
                            border: '1px dashed #777',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                            padding: 8,
                            flexShrink: 0
                        }}>
                            {selectedStudent?.photo_base64 ? (
                                <img
                                    src={selectedStudent.photo_base64}
                                    alt="Student"
                                    style={{ width: '100%', maxHeight: 150, objectFit: 'cover' }}
                                />
                            ) : (
                                <span style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
                                    {language === 'ar' ? 'صورة' : language === 'en' ? 'Photo' : 'Photo'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Middle Two-Column Section: À prévenir & Santé */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                        <tbody>
                            <tr>
                                {/* Left column: À prévenir */}
                                <td style={{ width: '50%', verticalAlign: 'top', border: 'none', paddingInlineEnd: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                                                <th colSpan={2} style={{ ...cellStyle(), fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {language === 'ar' ? 'جهات الاتصال في حالة الطوارئ' : language === 'en' ? 'In Case of Emergency Contact' : 'À prévenir'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الولي 1 — الاسم والهاتف' : language === 'en' ? 'Parent 1 — Name & Phone' : 'Parent 1 — nom et téléphone'}</td>
                                                <td style={cellStyle()}>{parent1Name}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الولي 2 — الاسم والهاتف' : language === 'en' ? 'Parent 2 — Name & Phone' : 'Parent 2 — nom et téléphone'}</td>
                                                <td style={cellStyle()}>{parent2Name}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'شخص آخر مخول' : language === 'en' ? 'Other authorized person' : 'Autre personne autorisée'}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'صلة القرابة' : language === 'en' ? 'Relationship' : 'Lien de parenté'}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Right column: Santé */}
                                <td style={{ width: '50%', verticalAlign: 'top', border: 'none', paddingInlineStart: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                                                <th colSpan={2} style={{ ...cellStyle(), fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {language === 'ar' ? 'الصحة' : language === 'en' ? 'Health' : 'Santé'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الحساسية' : language === 'en' ? 'Allergies' : 'Allergies'}</td>
                                                <td style={cellStyle()}>{allergies}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'مرض مزمن' : language === 'en' ? 'Chronic illness' : 'Maladie chronique'}</td>
                                                <td style={cellStyle()}>{chronic}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'علاج جاري' : language === 'en' ? 'Ongoing treatment' : 'Traitement en cours'}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'نظارات طبية' : language === 'en' ? 'Glasses wear' : 'Port de lunettes'}</td>
                                                <td style={cellStyle()}>[ ] {language === 'ar' ? 'نعم' : 'oui'} &nbsp; [ ] {language === 'ar' ? 'لا' : 'non'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Authorization Paragraph */}
                    <div style={{
                        marginTop: 15,
                        padding: 10,
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4,
                        fontSize: 12,
                        lineHeight: 1.6,
                        textAlign: 'justify'
                    }}>
                        {language === 'ar' ? (
                            'أرخّص أنا الولي للمؤسسة، في حالة الاستعجال وفي غيابي، بإجراء كل فحص أو علاج ضروري بما في ذلك النقل إلى أقرب مستشفى أو مركز صحي.'
                        ) : language === 'en' ? (
                            'I, the undersigned parent or legal guardian of the student, authorize the institution to administer, in case of emergency and in my absence, any necessary examination or treatment, including transport to the nearest hospital.'
                        ) : (
                            "Je soussigné(e), parent ou tuteur légal de l'apprenant, autorise l'établissement à faire pratiquer, en cas d'urgence et en mon absence, tout examen ou soin nécessaire, y compris le transport vers l'hôpital le plus proche."
                        )}
                    </div>

                    {/* Signatures */}
                    <table style={{ width: '100%', marginTop: 35, borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left' }}>
                                    <strong>{language === 'ar' ? 'الولي :' : language === 'en' ? 'Parent :' : 'Le parent :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '70%' }} />
                                </td>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right' }}>
                                    <strong>{language === 'ar' ? 'المدير (الختم) :' : language === 'en' ? 'Director (Stamp) :' : 'Le directeur (cachet) :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '70%', marginInlineStart: 'auto' }} />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 30, textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {language === 'ar' ? 'شكراً على ثقتكم.' : language === 'en' ? 'Thank you for your trust.' : 'Merci de votre confiance.'}
                    </div>
                </Box>
            </div>
        </GenericPage>
    );
};

export default PrintEmergencyFormPage;
